import {
  faCheck,
  faEdit,
  faQuestionCircle,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { type FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import {
  Box,
  Flex,
  Text,
  TextInput,
  Tooltip,
  Select,
  Button,
} from '@mantine/core'

import './styles.module.css'

import { useDebouncedValue } from '@mantine/hooks'
import React, { useEffect, forwardRef, useMemo, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import { type CoreItemPricingTier } from '@flecto/types'
import { Badge, Icon, InputCurrency } from '@flecto/ui'
import { useCurrencyUtils } from '@flecto/utils'

import { useTaxesInfo } from '../../../taxes'
import { usePricingTierLabel } from '../../hooks/usePricingTierLabel'

export type SelectPricingTiersProps = {
  pricingTiersItem: CoreItemPricingTier[]
  taxRate?: string
  taxable?: boolean
  resetCustomValue?: boolean
  onChangePricingTiers: (pricingTiers: CoreItemPricingTier) => void
  searchable?: boolean
  open?: boolean
  resetPricingTier?: VoidFunction
}

export function SelectPricingTiers({
  pricingTiersItem,
  onChangePricingTiers,
  taxRate,
  taxable,
  resetCustomValue,
  searchable,
  open,
  resetPricingTier,
}: SelectPricingTiersProps) {
  const track = useAnalytics('segment')
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })
  const { t: tItems } = useTranslation('saas', { keyPrefix: 'items' })
  const { centsToCurrency } = useCurrencyUtils()
  const { userSettingShowPricesWithTaxes, getCalculatedAmounts } =
    useTaxesInfo()
  const [enableEdit, setEnableEdit] = useState(false)
  const [previousItemValue, setPreviousItemValue] = useState('')

  const [localPriceTiers, setLocalPriceTiers] =
    useState<CoreItemPricingTier[]>(pricingTiersItem)
  const [customValue, setCustomValue] = useState(0)
  const [customLabel, setCustomLabel] = useState('')
  const [debouncedCustomValue] = useDebouncedValue(customValue, 500)
  const [debouncedCustomLabel] = useDebouncedValue(customLabel, 500)

  const [selectedAux, setSelectedAux] = useState('')
  const [oldPriceValue, setOldPriceValue] = useState(0)
  const [oldPriceLabel, setOldPriceLabel] = useState('')

  const [formTiers, setFormTiers] = useState({} as Partial<CoreItemPricingTier>)
  const generateUniqueId = (item: CoreItemPricingTier) =>
    `${item.duration_unit ?? ''}-${item.price_cents}-${item.type ?? ''}-${item.duration_value}`

  const pricingTiersMap = new Map<string | number, CoreItemPricingTier>()

  localPriceTiers?.forEach((item) => {
    const key = generateUniqueId(item)
    pricingTiersMap.set(key, item)
  })
  const [valueCustomItem, setValueCustomItem] = useState('')

  const translateLabel = (label: string) => {
    const regex = /\((\d+) (day|days|hour|hours)\)/
    const match = label.match(regex)
    if (match) {
      const [, number, unit] = match
      const translatedUnit = tItems(`overtime_charge.${unit}`)
      return `(${number} ${translatedUnit})`
    }
  }

  const optionPricingTiers = useMemo(() => {
    return localPriceTiers?.map((tier) => {
      const priceCents = userSettingShowPricesWithTaxes
        ? tier.price_with_vat_cents
        : tier.price_cents
      const price = priceCents ? centsToCurrency(priceCents) : '0,00'
      const itemLabel = `${usePricingTierLabel({
        itemTier: tier,
        tiers: pricingTiersItem,
      })}`

      const tLabel = !tier.label ? translateLabel(itemLabel) : itemLabel

      return {
        value: generateUniqueId(tier),
        label: `€ ${price} ${tLabel || ''}`,
        price: `${price}`,
        description: `${tLabel || ''}`,
        image: faQuestionCircle,
        overtimed: tier.type === 'dynamic',
        custom: tier.type === 'custom',
        selected: tier.selected,
        sugested: tier.suggested,
      }
    })
  }, [localPriceTiers])
  interface ItemProps extends React.ComponentPropsWithoutRef<'div'> {
    image: FontAwesomeIconProps['icon']
    label: string
    description: string
    value?: string
    selected?: boolean
    sugested?: boolean
    price?: string
    overtimed?: boolean
    custom?: boolean
  }

  const SelectItem = forwardRef<HTMLDivElement, ItemProps>(
    (
      {
        image,
        label,
        value,
        price,
        description,
        overtimed,
        custom,
        selected,
        sugested,
        ...others
      }: ItemProps,
      ref
    ) => (
      <div ref={ref} {...others} style={{ width: '100%' }}>
        <Flex align='center' gap='sm'>
          <Flex justify='space-between' align='center' w='100%'>
            <Flex align='center' gap='sm'>
              <Text>€ {price}</Text>
              <Text c='gray.7'>{description}</Text>
              {overtimed && (
                <Tooltip
                  label={
                    <Flex justify='center' align='center' wrap='wrap'>
                      {t('tooltip_custom_price_tiers_description')}
                    </Flex>
                  }
                  withinPortal
                  zIndex={999999}
                >
                  <Box>
                    <Icon icon={image} size='xs' />
                  </Box>
                </Tooltip>
              )}
            </Flex>
            <Flex align='center' gap='sm'>
              {sugested && (
                <Badge mx='sm' color='gray'>
                  {t('label_suggested')}
                </Badge>
              )}
              {custom && <Badge color='green'>{t('label_badge_custom')}</Badge>}
              {selected && <Icon icon={faCheck} size='xs' color='green' />}
            </Flex>
          </Flex>
        </Flex>
      </div>
    )
  )

  SelectItem.displayName = 'SelectItem'

  const handleEdit = (edit: boolean) => {
    if (valueCustomItem && valueCustomItem !== '') {
      setPreviousItemValue(valueCustomItem)
      if (edit) {
        setEnableEdit(true)
        track('BOOKING_ITEM__TIER_EDIT', {})
      }
      setOldPriceValue(customValue)
      setOldPriceLabel(customLabel)

      const customPricing = pricingTiersMap.get(valueCustomItem)

      setFormTiers({
        price_cents: customPricing?.price_cents,
        type: customPricing?.type,
        ...(customPricing?.label == null
          ? {
              duration_unit: customPricing?.duration_unit,
              duration_value: customPricing?.duration_value,
            }
          : {}),
        ...(customPricing?.label !== null && customPricing?.type === 'custom'
          ? { label: customPricing.label }
          : {}),
      })
      setCustomValue(
        Number(
          userSettingShowPricesWithTaxes
            ? customPricing?.price_with_vat_cents
            : customPricing?.price_cents
        )
      )
      setCustomLabel(
        String(
          customPricing?.label
            ? customPricing?.label ||
                usePricingTierLabel({
                  itemTier: customPricing as CoreItemPricingTier,
                })?.replace(/[()]/g, '')
            : customPricing?.duration_unit === 'days'
              ? customPricing?.duration_value === 1
                ? tItems('overtime_charge.day')
                : tItems('overtime_charge.days')
              : customPricing?.duration_value === 1
                ? tItems('overtime_charge.hour')
                : tItems('overtime_charge.hours')
        )
      )
    }

    setSelectedAux(valueCustomItem)
  }

  useEffect(() => {
    const updatedTiers = localPriceTiers?.map((tier) => {
      const itemLabel = generateUniqueId(tier)
      if (itemLabel === previousItemValue) {
        return {
          ...tier,
          selected: true,
        }
      }
      return { ...tier, selected: false }
    })

    setLocalPriceTiers(updatedTiers)
  }, [selectedAux])

  const handleSave = () => {
    let newItem: CoreItemPricingTier | null = null
    let customItemIndex = -1

    const updatedTiers = localPriceTiers?.map((tier, index) => {
      if (tier.type === 'custom') {
        customItemIndex = index
      }

      const itemLabel = generateUniqueId(tier)

      if (itemLabel === previousItemValue) {
        const amounts = getCalculatedAmounts(
          customValue,
          taxRate ?? '',
          taxable ?? true,
          true,
          userSettingShowPricesWithTaxes
        )

        newItem = {
          ...tier,
          price_cents: amounts.totalWithoutTaxes.cents,
          price_with_vat_cents: amounts.totalWithTaxes.cents,
          label: `${customLabel}`,
          selected: true,
          suggested: false,
          type: 'custom',
        }
      }

      return { ...tier, selected: false }
    })

    if (newItem) {
      if (customItemIndex >= 0) {
        updatedTiers[customItemIndex] = newItem
      } else {
        updatedTiers.push(newItem)
      }
    }

    setLocalPriceTiers(updatedTiers)

    updatedTiers.forEach((item) => {
      if (item.selected) {
        track('BOOKING_ITEM__TIER_CHANGED', {
          oldPriceValue: oldPriceValue ?? 0,
          oldPriceLabel: oldPriceLabel ?? '',
          newPriceValue:
            (userSettingShowPricesWithTaxes
              ? item?.price_with_vat_cents
              : item?.price_cents) ?? 0,
          newPriceLabel: item?.label ?? '',
        })
        setValueCustomItem(generateUniqueId(item))

        setFormTiers({
          price_cents: item?.price_cents,
          type: item?.type,
          ...(item?.label == null
            ? {
                duration_unit: item.duration_unit,
                duration_value: item.duration_value,
              }
            : {}),
          ...(item?.label !== null && item.type === 'custom'
            ? { label: item.label }
            : {}),
        })
      }
    })

    onChangePricingTiers(formTiers)
  }

  const handleResetValues = () => {
    if (resetPricingTier) {
      resetPricingTier()
    }
    setEnableEdit(false)

    pricingTiersItem?.forEach((item) => {
      setLocalPriceTiers(pricingTiersItem)
      if (item.selected) {
        setValueCustomItem(generateUniqueId(item))
      }
    })
  }

  useEffect(() => {
    handleEdit(false)
  }, [valueCustomItem])

  useEffect(() => {
    onChangePricingTiers(formTiers)
  }, [customLabel, customValue, formTiers])

  useEffect(() => {
    pricingTiersItem?.forEach((item) => {
      setLocalPriceTiers(pricingTiersItem)
      if (item.selected) {
        setValueCustomItem(generateUniqueId(item))
      }
    })

    setEnableEdit(false)
  }, [resetCustomValue])

  useEffect(() => {
    if (enableEdit === true) {
      handleSave()
    }
  }, [debouncedCustomValue, debouncedCustomLabel])

  return (
    <Flex
      align='center'
      style={{ cursor: 'default', display: 'inline-flex' }}
      w={'100%'}
    >
      {enableEdit === true ? (
        <Flex gap='md' mt='sm' wrap='wrap' w={'100%'}>
          <InputCurrency
            style={{ flex: 1 }}
            data-testid={'input-cents-customize'}
            value={customValue}
            min={0}
            onChange={setCustomValue}
          />
          <TextInput
            data-testid={'input-label-customize'}
            value={customLabel}
            style={{ flex: 1 }}
            onChange={(e) => setCustomLabel(e.target.value)}
          />
          <Button
            variant='default'
            data-testid='btn-close'
            onClick={handleResetValues}
            size='md'
            w='42px'
            styles={() => ({
              section: {
                marginRight: 0,
              },
            })}
            leftSection={<Icon icon={faXmark} size='sm' color='gray' />}
          />{' '}
        </Flex>
      ) : (
        <>
          <Flex gap='md' mt='sm' wrap='wrap' w={'100%'}>
            <Select
              defaultDropdownOpened={open}
              data={optionPricingTiers}
              data-testid='select-pricing-tiers'
              // @ts-expect-error Missing type
              renderOption={(item) => <SelectItem {...item.option} />}
              defaultValue={valueCustomItem}
              value={valueCustomItem}
              searchable={searchable}
              w={'100%'}
              onChange={(value) => {
                if (value) {
                  setValueCustomItem(value)
                }
              }}
            />{' '}
          </Flex>

          <Flex align='center'>
            <Button
              variant='default'
              ml='sm'
              mt='sm'
              style={{ border: 'none' }}
              onClick={() => handleEdit(true)}
            >
              <Icon
                data-testid='btn-edit'
                color='gray'
                icon={faEdit}
                size='xs'
              />
              <Text c='dimmed' ml='sm'>
                {t('button_edit')}
              </Text>
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  )
}
